import React from "react";
import { Link } from "react-router-dom";
import { Calendar, User, ArrowRight, Clock } from "lucide-react";

interface Props {
  currentPosts: any[];
  onBlogClick?: (blog: any) => void;
}

const cleanHtml = (html?: string) => {
  if (!html) return "";
  const doc = new DOMParser().parseFromString(html, "text/html");
  return doc.body.textContent?.trim() || "";
};

const truncateText = (text: string, maxLength: number) => {
  if (!text) return "";
  return text.length > maxLength ? text.slice(0, maxLength) + "..." : text;
};

// Calculate approximate reading time
const getReadingTime = (text: string) => {
  const words = text.trim().split(/\s+/).length;
  const time = Math.ceil(words / 180);
  return `${Math.max(1, time)} min read`;
};

export default function BlogPostsGrid({ currentPosts, onBlogClick }: Props) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-12">
      {currentPosts.map((post) => {
        const plainText = cleanHtml(post.content || post.description);
        const readingTime = getReadingTime(plainText);

        return (
          <Link
            key={post._id}
            to={`/blog/${post._id}`}
            state={{ blog: post }}
            onClick={() => onBlogClick && onBlogClick(post)}
            className="group bg-white rounded-2xl shadow-sm hover:shadow-xl border border-[#2C4A3B]/10 hover:border-[#2C4A3B]/30 transition-all duration-300 flex flex-col overflow-hidden hover:-translate-y-1.5 cursor-pointer"
          >
            {/* Thumbnail */}
            <div className="relative overflow-hidden aspect-[16/10] bg-gray-100">
              <img
                src={post.thumbnailUrl || "/metalogo.webp"}
                alt={post.title}
                className="w-full h-full object-cover object-top transition-transform duration-500 ease-out group-hover:scale-105"
                loading="lazy"
              />

              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />

              {post.category && (
                <div className="absolute top-3.5 left-3.5 z-10">
                  <span className="bg-white/95 backdrop-blur-md text-[#2C4A3B] px-3 py-1 rounded-full text-xs font-bold tracking-wide shadow-sm border border-[#2C4A3B]/10">
                    {post.category}
                  </span>
                </div>
              )}
            </div>

            {/* Content */}
            <div className="p-5 sm:p-6 flex-1 flex flex-col justify-between">
              <div>
                {/* Meta Bar */}
                <div className="flex items-center gap-3 text-xs text-[#556963] mb-2.5">
                  <div className="flex items-center gap-1">
                    <Calendar size={13} className="text-[#C69248]" />
                    <span>
                      {post.createdAt
                        ? new Date(post.createdAt).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            year: "numeric"
                          })
                        : "Recent"}
                    </span>
                  </div>
                  <span>•</span>
                  <div className="flex items-center gap-1">
                    <Clock size={13} className="text-[#C69248]" />
                    <span>{readingTime}</span>
                  </div>
                </div>

                {/* Title */}
                <h3 className="text-lg sm:text-xl font-bold text-[#15302B] mb-2.5 leading-snug group-hover:text-[#2C4A3B] transition-colors line-clamp-2">
                  {post.title}
                </h3>

                {/* Excerpt */}
                <p className="text-sm text-[#4A5D57] leading-relaxed line-clamp-3 mb-4">
                  {truncateText(plainText, 120)}
                </p>
              </div>

              {/* Bottom Footer */}
              <div className="pt-3 border-t border-gray-100 flex items-center justify-between mt-auto">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-[#E8F0EC] flex items-center justify-center text-[#2C4A3B]">
                    <User size={12} />
                  </div>
                  <span className="text-xs font-medium text-[#3B5750]">
                    Editorial Team
                  </span>
                </div>

                <div className="inline-flex items-center gap-1 text-xs sm:text-sm font-semibold text-[#2C4A3B] group-hover:translate-x-1 transition-transform duration-200">
                  <span>Read Article</span>
                  <ArrowRight size={14} />
                </div>
              </div>
            </div>
          </Link>
        );
      })}
    </div>
  );
}

